import { Fragment, useState, useMemo, useCallback } from 'react';
import { NavIcon, RightDrawer, useBrandTokens } from '../../../shell/shared';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qTreatmentRecordsByPatient,
  qEncountersByPatient,
  qEncounterNotesPreviews,
  qInvoicesByPatient,
  qBillingAdjustmentsByPatient,
  qPaymentsByPatient,
  qInvoiceItemRemarksByPatient,
  parsePaymentAllocations,
  type PatientTreatmentRecordRow,
  type PatientEncounterRow,
  type EncounterNotePreviewRow,
  type InvoiceRow,
  type BillingAdjustmentRow,
  type InvoicePaymentRow,
  type InvoiceItemRemarkRow,
} from '@helios/data-access';
import { TreatmentRecordDrawer } from './TreatmentRecordDrawer';
import { NotePreview } from './timeline-previews';
import { TreatmentRecordQuickToolbarHost } from './TreatmentRecordQuickToolbarHost';
import { useFormatMoney } from '../../../hooks/useFormatMoney';
import { usePatientBilling } from '../../billing/usePatientBilling';
import { InvoicesTab } from '../consultation/InvoicesTab';

// ─── Helpers ───

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Running-balance color: owed (positive) is red, settled/credit is green, zero
// is neutral — matching the header Total Balance treatment.
function runningBalanceColor(cents: number): string {
  return cents > 0 ? '#dc2626' : cents < 0 ? '#16a34a' : 'var(--text)';
}

// ─── Types ───

/**
 * PRD.246 — a derived, view-only payment row in the Treatment Record ledger.
 * Reflects a single item-level payment allocation on the date it was paid.
 */
interface PaymentLedgerRow {
  id: string;
  date: string; // YYYY-MM-DD (payment date)
  sortTs: string; // full timestamp for chronological ordering
  tooth_nos: string | null;
  procedure_name: string;
  note: string;
  paidCents: number;
  invoiceId: string | null;
}

// ─── Props ───

interface TreatmentRecordViewProps {
  patientId: string;
  addRecordRef?: React.MutableRefObject<(() => void) | null>;
}

// ─── Component ───

export function TreatmentRecordView({ patientId, addRecordRef }: TreatmentRecordViewProps) {
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const fmt = useFormatMoney();

  // TreatmentRecord row amounts are stored as cents; convert at the boundary.
  const fmtCents = (cents: number) => fmt(cents / 100);

  const { data: records = [] } = usePowerSyncQuery<PatientTreatmentRecordRow>(
    qTreatmentRecordsByPatient(patientId),
  );
  const { data: encounterRows = [] } = usePowerSyncQuery<PatientEncounterRow>(
    qEncountersByPatient(patientId),
  );
  const { data: notesPreviews = [] } = usePowerSyncQuery<EncounterNotePreviewRow>(
    qEncounterNotesPreviews(patientId),
  );
  const { data: invoiceRows = [] } = usePowerSyncQuery<InvoiceRow>(qInvoicesByPatient(patientId));
  // PRD.245 follow-up — reflect the LATEST invoice notes on linked invoice rows
  // (live, not the stored snapshot): per-line remarks from invoice_items and the
  // invoice-level billing note from invoices.
  const { data: itemRemarkRows = [] } = usePowerSyncQuery<InvoiceItemRemarkRow>(
    qInvoiceItemRemarksByPatient(patientId),
  );
  const itemRemarksById = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of itemRemarkRows) {
      const t = r.remarks?.trim();
      if (t) map.set(r.id, t);
    }
    return map;
  }, [itemRemarkRows]);
  const invoiceNoteById = useMemo(() => {
    const map = new Map<string, string>();
    for (const inv of invoiceRows) {
      const t = inv.notes?.trim();
      if (t) map.set(inv.id, t);
    }
    return map;
  }, [invoiceRows]);
  // PRD.243 — reflected adjustment rows store zero amounts; their authoritative
  // figure lives in billing_adjustments. Look it up by id for accurate display.
  const { data: adjustmentRows = [] } = usePowerSyncQuery<BillingAdjustmentRow>(
    qBillingAdjustmentsByPatient(patientId),
  );
  const adjustmentsById = useMemo(() => {
    const map: Record<string, BillingAdjustmentRow> = {};
    for (const a of adjustmentRows) map[a.id] = a;
    return map;
  }, [adjustmentRows]);

  // PRD.245 — reflect item-level payment allocation into linked treatment
  // records. Paid/balance for an invoice-item-linked row is DERIVED from real
  // billing allocations (invoice_payments.allocations) — billing stays the
  // source of truth. We never guess from unallocated partial payments
  // (PRD.245 §5.5): only explicit allocations move an item's paid, except a
  // fully-settled invoice unambiguously settles all its items.
  const { data: paymentRows = [] } = usePowerSyncQuery<InvoicePaymentRow>(
    qPaymentsByPatient(patientId),
  );
  const reflection = useMemo(() => {
    const allocByItem = new Map<string, number>(); // invoice_item_id → allocated cents
    const paidByInvoice = new Map<string, number>(); // invoice_id → total paid cents
    for (const p of paymentRows) {
      if (p.invoice_id) {
        paidByInvoice.set(
          p.invoice_id,
          (paidByInvoice.get(p.invoice_id) ?? 0) + (p.amount_cents ?? 0),
        );
      }
      for (const a of parsePaymentAllocations(p.allocations)) {
        allocByItem.set(a.item_id, (allocByItem.get(a.item_id) ?? 0) + a.amount_cents);
      }
    }
    const chargeByInvoice = new Map<string, number>(); // invoice_id → total charge cents
    for (const inv of invoiceRows) {
      chargeByInvoice.set(inv.id, Math.round((inv.grand_total ?? 0) * 100));
    }
    return { allocByItem, paidByInvoice, chargeByInvoice };
  }, [paymentRows, invoiceRows]);

  // Reflected paid (cents) for a treatment record row. Linked invoice-item
  // rows derive from allocations; manual rows keep their stored value.
  const reflectedPaidCents = useCallback(
    (r: PatientTreatmentRecordRow): number => {
      if (!r.invoice_item_id) return r.amount_paid_cents ?? 0;
      const charge = r.amount_charged_cents ?? 0;
      const invId = r.invoice_id;
      const invoiceCharge = invId ? (reflection.chargeByInvoice.get(invId) ?? 0) : 0;
      const invoicePaid = invId ? (reflection.paidByInvoice.get(invId) ?? 0) : 0;
      // Whole invoice settled → every linked item is certainly fully paid.
      if (invoiceCharge > 0 && invoicePaid >= invoiceCharge) return charge;
      // Otherwise only real allocations count (no guessing from unallocated).
      const allocated = reflection.allocByItem.get(r.invoice_item_id) ?? 0;
      return Math.min(charge, allocated);
    },
    [reflection],
  );

  // PRD.246 — derive chronological "payment rows" for the ledger view so the
  // running Balance reflects EVERY payment (otherwise unallocated payments are
  // invisible and the balance reads too high). For each payment:
  //   - each allocation linked to a charged treatment-record item gets a row
  //     referencing that item (Charged 0, Paid = allocated amount);
  //   - the remaining amount (unallocated, or allocated to an unlinked item) is
  //     emitted as ONE generic invoice-level payment row. It reduces the balance
  //     without claiming a specific item is paid — so we stay honest about
  //     item-level certainty (PRD.246 §5.8) while keeping the balance correct.
  // Balance itself is the account-wide running balance (see runningBalanceByRow).
  const paymentLedgerRows = useMemo<PaymentLedgerRow[]>(() => {
    // invoice_item_id → the charged treatment record row it settled.
    const chargedByItem = new Map<string, PatientTreatmentRecordRow>();
    for (const r of records) {
      if (r.invoice_item_id) chargedByItem.set(r.invoice_item_id, r);
    }
    const invoiceNoById = new Map<string, string>();
    for (const inv of invoiceRows) invoiceNoById.set(inv.id, inv.invoice_no ?? '');

    const out: PaymentLedgerRow[] = [];
    for (const p of paymentRows) {
      const ts = p.paid_at ?? p.created_at ?? '';
      const date = ts.slice(0, 10);
      const payNote = p.notes?.trim();
      let shownCents = 0; // amount represented by item-linked allocation rows

      for (const a of parsePaymentAllocations(p.allocations)) {
        const charged = chargedByItem.get(a.item_id);
        if (!charged) continue; // unlinked allocation → folds into the remainder
        shownCents += a.amount_cents;
        const reference = `Payment [${charged.procedure_name}${charged.date ? ` - ${formatDate(charged.date)}` : ''}]`;
        out.push({
          id: `pay:${p.id}:${a.item_id}`,
          date,
          sortTs: ts,
          tooth_nos: charged.tooth_nos ?? null,
          procedure_name: charged.procedure_name,
          note: payNote ? `${reference} — ${payNote}` : reference,
          paidCents: a.amount_cents,
          invoiceId: p.invoice_id ?? charged.invoice_id ?? null,
        });
      }

      // Remainder = the part of the payment not tied to a linked item. Emit a
      // single invoice-level payment row so the balance still reflects it.
      const remainder = (p.amount_cents ?? 0) - shownCents;
      if (remainder > 0) {
        const invNo = p.invoice_id ? invoiceNoById.get(p.invoice_id) : '';
        const reference = `Payment${invNo ? ` [${invNo}]` : ' received'}`;
        out.push({
          id: `pay:${p.id}:unallocated`,
          date,
          sortTs: ts,
          tooth_nos: null,
          procedure_name: '',
          note: payNote ? `${reference} — ${payNote}` : reference,
          paidCents: remainder,
          invoiceId: p.invoice_id ?? null,
        });
      }
    }
    return out;
  }, [paymentRows, records, invoiceRows]);

  // PRD.246 (running-balance variant) — account-wide running balance keyed by
  // rendered row id. Walks every financial event chronologically and
  // accumulates a signed delta matching the billing aggregate formula
  // (Balance = Charges + Debits − Payments − Credits):
  //   • procedure charge → +charged (manual rows also −stored paid; invoice-
  //     item rows leave paid to their derived payment rows to avoid double count)
  //   • derived payment row → −allocated amount
  //   • adjustment → +debit / −credit
  // Same-instant ties order charge → adjustment → payment so a balance never
  // dips before its charge lands.
  const { balanceByRow, orderByRow } = useMemo(() => {
    // Order by the event's BUSINESS date (treatment date / payment date), NOT
    // the row's created_at — records can be entered/synced long after the fact,
    // which would otherwise scramble the chronology. `seq` only breaks ties
    // among same-date, same-rank events.
    type Ev = { key: string; date: string; rank: number; seq: string; delta: number };
    const events: Ev[] = [];
    for (const r of records) {
      const seq = r.created_at ?? '';
      if (r.kind === 'adjustment') {
        const adj = r.billing_adjustment_id ? adjustmentsById[r.billing_adjustment_id] : null;
        const amt = adj?.amount_cents ?? 0;
        const isDebit = adj ? adj.adjustment_type === 'debit' : /debit/i.test(r.procedure_name);
        events.push({ key: r.id, date: r.date, rank: 1, seq, delta: isDebit ? amt : -amt });
      } else {
        const charged = r.amount_charged_cents ?? 0;
        const paid = r.invoice_item_id ? 0 : (r.amount_paid_cents ?? 0);
        events.push({ key: r.id, date: r.date, rank: 0, seq, delta: charged - paid });
      }
    }
    for (const pr of paymentLedgerRows) {
      events.push({ key: pr.id, date: pr.date, rank: 2, seq: pr.sortTs, delta: -pr.paidCents });
    }
    events.sort((a, b) =>
      a.date !== b.date
        ? a.date < b.date
          ? -1
          : 1
        : a.rank !== b.rank
          ? a.rank - b.rank
          : a.seq < b.seq
            ? -1
            : a.seq > b.seq
              ? 1
              : 0,
    );
    const balanceByRow = new Map<string, number>();
    // Chronological index per row (0 = oldest). Drives newest-first display
    // ordering within a day so the Balance column reads monotonically.
    const orderByRow = new Map<string, number>();
    let running = 0;
    events.forEach((e, i) => {
      running += e.delta;
      balanceByRow.set(e.key, running);
      orderByRow.set(e.key, i);
    });
    return { balanceByRow, orderByRow };
  }, [records, paymentLedgerRows, adjustmentsById]);

  const paymentRowsByDate = useMemo(() => {
    const map = new Map<string, PaymentLedgerRow[]>();
    for (const pr of paymentLedgerRows) {
      const list = map.get(pr.date);
      if (list) list.push(pr);
      else map.set(pr.date, [pr]);
    }
    return map;
  }, [paymentLedgerRows]);

  // Group all notes by their encounter so each treatment row can show the
  // full notes recorded on its visit note (combined, in chronological order).
  const notesByEncounter = useMemo(() => {
    const map: Record<string, EncounterNotePreviewRow[]> = {};
    for (const n of notesPreviews) {
      if (!map[n.encounter_id]) map[n.encounter_id] = [];
      map[n.encounter_id].push(n);
    }
    return map;
  }, [notesPreviews]);

  // Invoice → encounter fallback: many treatment records are auto-created
  // from invoice items and have `invoice_id` but no direct `encounter_id`,
  // so resolve through the invoice to reach the visit's notes.
  const invoiceToEncounter = useMemo(() => {
    const map: Record<string, string> = {};
    for (const inv of invoiceRows) {
      if (inv.encounter_id) map[inv.id] = inv.encounter_id;
    }
    return map;
  }, [invoiceRows]);

  // Date → encounters fallback: manually added treatment records often have
  // neither encounter_id nor invoice_id. Fall back to any encounter(s) on
  // the same day so their visit-note content can still surface.
  const encountersByDate = useMemo(() => {
    const map: Record<string, PatientEncounterRow[]> = {};
    for (const e of encounterRows) {
      const d = (e.started_at ?? e.created_at ?? '').slice(0, 10);
      if (!d) continue;
      if (!map[d]) map[d] = [];
      map[d].push(e);
    }
    return map;
  }, [encounterRows]);

  // Collect every encounter id a treatment record could be tied to. When
  // the record carries no direct link, the date fallback may yield multiple
  // encounters — we surface notes from all of them.
  const resolveEncounterIds = useCallback(
    (r: PatientTreatmentRecordRow): string[] => {
      if (r.encounter_id) return [r.encounter_id];
      if (r.invoice_id && invoiceToEncounter[r.invoice_id]) {
        return [invoiceToEncounter[r.invoice_id]];
      }
      const sameDay = encountersByDate[r.date];
      if (sameDay && sameDay.length > 0) return sameDay.map((e) => e.id);
      return [];
    },
    [invoiceToEncounter, encountersByDate],
  );

  // Group records by date and surface dates that have notes / invoices but
  // no procedures yet, so quick-toolbar entries (PRD.182) immediately show
  // up here instead of being hidden until a procedure is added.
  const recordsByDate = useMemo(() => {
    // Start with dates that have treatment records, preserving the
    // already-sorted (date DESC, created_at DESC) order from the query.
    const groups: { date: string; rows: PatientTreatmentRecordRow[] }[] = [];
    const seenDates = new Set<string>();
    for (const r of records) {
      const last = groups[groups.length - 1];
      if (last && last.date === r.date) {
        last.rows.push(r);
      } else {
        groups.push({ date: r.date, rows: [r] });
      }
      seenDates.add(r.date);
    }

    // Collect extra dates from encounters that have notes or invoices
    // attached, so a day with only documentation still appears.
    const extraDates = new Set<string>();
    const noteEncounterIds = new Set(notesPreviews.map((n) => n.encounter_id));
    const invoiceEncounterIds = new Set(
      invoiceRows.filter((i) => i.encounter_id).map((i) => i.encounter_id as string),
    );
    for (const e of encounterRows) {
      const d = (e.started_at ?? e.created_at ?? '').slice(0, 10);
      if (!d || seenDates.has(d)) continue;
      if (noteEncounterIds.has(e.id) || invoiceEncounterIds.has(e.id)) {
        extraDates.add(d);
      }
    }
    for (const d of extraDates) groups.push({ date: d, rows: [] });

    // Final sort: newest first.
    groups.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
    return groups;
  }, [records, notesPreviews, invoiceRows, encounterRows]);

  // PRD.246 — fold derived payment rows into the date groups. A payment made on
  // a day with no other treatment activity gets its own group.
  const dateGroups = useMemo(() => {
    const groups = recordsByDate.map((g) => ({
      ...g,
      payments: paymentRowsByDate.get(g.date) ?? [],
    }));
    const seen = new Set(groups.map((g) => g.date));
    for (const [date, payments] of paymentRowsByDate) {
      if (!seen.has(date)) groups.push({ date, rows: [], payments });
    }
    groups.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
    return groups;
  }, [recordsByDate, paymentRowsByDate]);

  const collectDayNotes = useCallback(
    (date: string, rows: PatientTreatmentRecordRow[]): EncounterNotePreviewRow[] => {
      const seen = new Set<string>();
      const out: EncounterNotePreviewRow[] = [];
      // Resolve encounter ids from treatment records in the group.
      for (const r of rows) {
        for (const eid of resolveEncounterIds(r)) {
          for (const n of notesByEncounter[eid] ?? []) {
            const key = n.created_at ?? `${n.encounter_id}:${n.title}:${n.body}`;
            if (seen.has(key)) continue;
            seen.add(key);
            out.push(n);
          }
        }
      }
      // Also include notes for any encounter on this date (covers note-only
      // days and notes the date-fallback in resolveEncounterIds would miss).
      for (const e of encountersByDate[date] ?? []) {
        for (const n of notesByEncounter[e.id] ?? []) {
          const key = n.created_at ?? `${n.encounter_id}:${n.title}:${n.body}`;
          if (seen.has(key)) continue;
          seen.add(key);
          out.push(n);
        }
      }
      return out;
    },
    [notesByEncounter, resolveEncounterIds, encountersByDate],
  );

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<PatientTreatmentRecordRow | null>(null);
  // PRD.243 — invoice-derived / adjustment rows link to their invoice. Clicking
  // the link opens the same invoice detail drawer the Billing tab uses, in place
  // (without switching tabs).
  const [drawerInvoiceId, setDrawerInvoiceId] = useState<string | null>(null);
  const drawerInvoice = useMemo(
    () =>
      drawerInvoiceId ? (invoiceRows.find((inv) => inv.id === drawerInvoiceId) ?? null) : null,
    [drawerInvoiceId, invoiceRows],
  );

  // Expose add-record trigger to parent
  if (addRecordRef) {
    addRecordRef.current = () => {
      setEditRecord(null);
      setDrawerOpen(true);
    };
  }

  // Totals — PRD.243: mirror the authoritative billing aggregate (the same
  // hook the sidebar Billing card and header balance chip consume) so the
  // Treatment Record header can never drift from the patient's true
  // outstanding. Billing stays the source of truth; this only displays it
  // (no balance math is recomputed from treatment-record rows). This is why
  // reflected adjustment rows carry zero charged/paid — they must not be
  // double-counted here.
  const billing = usePatientBilling(patientId);
  const totals = useMemo(
    () => ({
      charged: billing.chargedCents,
      paid: billing.paidCents,
      balance: billing.balanceCents,
    }),
    [billing.chargedCents, billing.paidCents, billing.balanceCents],
  );

  // Encounter options for drawer
  const encounterOptions = useMemo(
    () =>
      encounterRows.map((e) => ({
        id: e.id,
        label: `${e.type ?? 'Encounter'} — ${e.started_at ? new Date(e.started_at).toLocaleDateString() : 'No date'}`,
      })),
    [encounterRows],
  );

  const handleSave = useCallback(
    async (data: {
      date: string;
      tooth_nos: string;
      procedure_name: string;
      amount_charged_cents: number;
      amount_paid_cents: number;
      next_appointment_at: string;
      notes: string;
      encounter_id: string;
    }) => {
      if (!db) return;
      const now = new Date().toISOString();

      if (editRecord) {
        // Update
        await db.execute(
          `UPDATE patient_treatment_records SET date = ?, tooth_nos = ?, procedure_name = ?, amount_charged_cents = ?, amount_paid_cents = ?, next_appointment_at = ?, notes = ?, encounter_id = ?, updated_at = ? WHERE id = ?`,
          [
            data.date,
            data.tooth_nos || null,
            data.procedure_name,
            data.amount_charged_cents,
            data.amount_paid_cents,
            data.next_appointment_at || null,
            data.notes || null,
            data.encounter_id || null,
            now,
            editRecord.id,
          ],
        );
      } else {
        // Create
        const id = crypto.randomUUID();
        await db.execute(
          `INSERT INTO patient_treatment_records (id, org_id, clinic_id, patient_id, encounter_id, date, tooth_nos, procedure_name, amount_charged_cents, amount_paid_cents, next_appointment_at, notes, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            id,
            orgId,
            null,
            patientId,
            data.encounter_id || null,
            data.date,
            data.tooth_nos || null,
            data.procedure_name,
            data.amount_charged_cents,
            data.amount_paid_cents,
            data.next_appointment_at || null,
            data.notes || null,
            now,
            now,
          ],
        );
      }
      setEditRecord(null);
    },
    [db, orgId, patientId, editRecord],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      if (!db) return;
      await db.execute(`DELETE FROM patient_treatment_records WHERE id = ?`, [id]);
    },
    [db],
  );

  const thStyle = {
    padding: '10px 12px',
    textAlign: 'left' as const,
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-semi)',
    color: 'var(--text-muted)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap' as const,
  };

  const tdStyle = {
    padding: '10px 12px',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--text)',
    borderBottom: '1px solid var(--border)',
    whiteSpace: 'nowrap' as const,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      {/* PRD.182 — shared Treatment Record Quick Toolbar */}
      <TreatmentRecordQuickToolbarHost patientId={patientId} />

      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'auto',
          background: 'var(--bg-surface)',
        }}
      >
        {/* Header: title + totals + Add Record */}
        <div
          style={{
            padding: 'var(--sp-4) var(--sp-5)',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 'var(--sp-4)',
          }}
        >
          <div>
            <div
              style={{
                fontSize: 'var(--font-size-base)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text)',
              }}
            >
              Treatment Record
            </div>
            <div
              style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginTop: 2 }}
            >
              Chronological dental procedures and payments
            </div>
            {/* Inline totals */}
            <div
              style={{
                display: 'flex',
                gap: 'var(--sp-4)',
                marginTop: 8,
                fontSize: 'var(--font-size-sm)',
              }}
            >
              <span>
                <span style={{ color: 'var(--text-muted)' }}>Total Charged: </span>
                <span style={{ fontWeight: 'var(--font-weight-semi)', color: 'var(--text)' }}>
                  {fmtCents(totals.charged)}
                </span>
              </span>
              <span>
                <span style={{ color: 'var(--text-muted)' }}>Total Paid: </span>
                <span style={{ fontWeight: 'var(--font-weight-semi)', color: '#16a34a' }}>
                  {fmtCents(totals.paid)}
                </span>
              </span>
              <span>
                <span style={{ color: 'var(--text-muted)' }}>Total Balance: </span>
                <span
                  style={{
                    fontWeight: 'var(--font-weight-semi)',
                    color:
                      totals.balance > 0
                        ? '#dc2626'
                        : totals.balance < 0
                          ? '#16a34a'
                          : 'var(--text)',
                  }}
                >
                  {fmtCents(totals.balance)}
                </span>
              </span>
            </div>
          </div>
          {!addRecordRef && (
            <button
              onClick={() => {
                setEditRecord(null);
                setDrawerOpen(true);
              }}
              style={{
                height: 36,
                padding: '0 16px',
                borderRadius: 'var(--radius-md)',
                border: 'none',
                background: primary,
                color: '#fff',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                flexShrink: 0,
              }}
            >
              <NavIcon name="plus" /> Add Record
            </button>
          )}
        </div>

        {/* Table */}
        <div>
          {dateGroups.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: 'var(--sp-8)',
                color: 'var(--text-muted)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              No treatment records yet. Click "+ Add Record" to create one.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Tooth No/s</th>
                  <th style={thStyle}>Procedure</th>
                  <th style={{ ...thStyle, minWidth: 240 }}>Notes</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Charged</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Paid</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Balance</th>
                  <th style={thStyle}>Next Appt</th>
                  <th style={{ ...thStyle, width: 70 }}></th>
                </tr>
              </thead>
              <tbody>
                {dateGroups.map(({ date, rows, payments }) => {
                  const dayNotes = collectDayNotes(date, rows);
                  // Display charge/adjustment rows newest-first (reverse of the
                  // running-balance accumulation order), matching how payment
                  // rows render, so the Balance column reads monotonically down
                  // the day instead of jumping between the payment and charge
                  // blocks.
                  const sortedRows = [...rows].sort(
                    (a, b) => (orderByRow.get(b.id) ?? 0) - (orderByRow.get(a.id) ?? 0),
                  );
                  // The day's visit notes render once as a standalone row at the
                  // top of the date group. The per-row Notes column is reserved
                  // for each invoice row's own notes (remarks + invoice-level
                  // billing note), so the two never compete for the same cell.
                  return (
                    <Fragment key={date}>
                      {dayNotes.length > 0 && (
                        <tr style={{ background: 'var(--bg-surface-2, #f9fafb)' }}>
                          <td
                            style={{
                              ...tdStyle,
                              verticalAlign: 'top',
                              fontWeight: 'var(--font-weight-medium)',
                            }}
                          >
                            {formatDate(date)}
                          </td>
                          <td style={{ ...tdStyle, color: 'var(--text-placeholder)' }}>—</td>
                          <td style={{ ...tdStyle, color: 'var(--text-placeholder)' }}>—</td>
                          <td
                            style={{
                              ...tdStyle,
                              whiteSpace: 'normal',
                              verticalAlign: 'top',
                              minWidth: 240,
                              maxWidth: 420,
                            }}
                          >
                            <DayNotesContent notes={dayNotes} />
                          </td>
                          <td
                            style={{
                              ...tdStyle,
                              textAlign: 'right',
                              color: 'var(--text-placeholder)',
                            }}
                          >
                            —
                          </td>
                          <td
                            style={{
                              ...tdStyle,
                              textAlign: 'right',
                              color: 'var(--text-placeholder)',
                            }}
                          >
                            —
                          </td>
                          <td
                            style={{
                              ...tdStyle,
                              textAlign: 'right',
                              color: 'var(--text-placeholder)',
                            }}
                          >
                            —
                          </td>
                          <td style={{ ...tdStyle, color: 'var(--text-placeholder)' }}>—</td>
                          <td style={tdStyle}></td>
                        </tr>
                      )}
                      {/* PRD.246 — payment activity happened after the charge,
                          so render derived payment rows ABOVE the charged rows
                          (newest-first within the day). */}
                      {[...payments]
                        .sort((a, b) => (orderByRow.get(b.id) ?? 0) - (orderByRow.get(a.id) ?? 0))
                        .map((pr) => (
                          <tr
                            key={pr.id}
                            style={{ background: 'var(--color-success-bg, #f0fdf4)' }}
                          >
                            <td style={tdStyle}>{formatDate(pr.date)}</td>
                            <td style={tdStyle}>{pr.tooth_nos || '—'}</td>
                            <td style={tdStyle}>
                              <span
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                              >
                                <span
                                  style={{
                                    padding: '2px 8px',
                                    borderRadius: 'var(--radius-full)',
                                    fontSize: 'var(--font-size-xs)',
                                    fontWeight: 'var(--font-weight-semi)',
                                    color: '#16a34a',
                                    background: 'var(--bg-surface)',
                                  }}
                                >
                                  Payment
                                </span>
                                <span style={{ color: 'var(--text-muted)' }}>
                                  {pr.procedure_name}
                                </span>
                              </span>
                            </td>
                            <td
                              style={{
                                ...tdStyle,
                                whiteSpace: 'normal',
                                color: 'var(--text-muted)',
                              }}
                            >
                              {pr.note}
                            </td>
                            <td
                              style={{
                                ...tdStyle,
                                textAlign: 'right',
                                color: 'var(--text-placeholder)',
                              }}
                            >
                              {fmtCents(0)}
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right', color: '#16a34a' }}>
                              {fmtCents(pr.paidCents)}
                            </td>
                            <td
                              style={{
                                ...tdStyle,
                                textAlign: 'right',
                                fontWeight: 'var(--font-weight-semi)',
                                color: runningBalanceColor(balanceByRow.get(pr.id) ?? 0),
                              }}
                            >
                              {fmtCents(balanceByRow.get(pr.id) ?? 0)}
                            </td>
                            <td style={{ ...tdStyle, color: 'var(--text-placeholder)' }}>—</td>
                            <td style={tdStyle}>
                              <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                                {pr.invoiceId ? (
                                  <InvoiceLinkButton
                                    primary={primary}
                                    onClick={() => setDrawerInvoiceId(pr.invoiceId)}
                                  />
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ))}
                      {sortedRows.map((r) => {
                        // PRD.243 — reflected billing adjustment: render as a
                        // distinct event row, not a procedure. Amount/type come
                        // from the linked billing_adjustments row (source of
                        // truth); fall back to the stored label if unavailable.
                        if (r.kind === 'adjustment') {
                          const adj = r.billing_adjustment_id
                            ? adjustmentsById[r.billing_adjustment_id]
                            : null;
                          const isDebit = adj
                            ? adj.adjustment_type === 'debit'
                            : /debit/i.test(r.procedure_name);
                          const amountCents = adj?.amount_cents ?? null;
                          const effectColor = isDebit ? '#dc2626' : '#16a34a';
                          const detail = [
                            amountCents != null
                              ? `${isDebit ? '+' : '−'}${fmtCents(amountCents)}`
                              : null,
                            r.notes,
                          ]
                            .filter(Boolean)
                            .join(' · ');
                          return (
                            <tr key={r.id} style={{ background: 'var(--bg-surface-2, #f9fafb)' }}>
                              <td style={tdStyle}>{formatDate(r.date)}</td>
                              <td style={{ ...tdStyle, color: 'var(--text-placeholder)' }}>—</td>
                              <td style={tdStyle}>
                                <span
                                  style={{
                                    display: 'inline-block',
                                    padding: '2px 8px',
                                    borderRadius: 'var(--radius-full)',
                                    fontSize: 'var(--font-size-xs)',
                                    fontWeight: 'var(--font-weight-semi)',
                                    color: effectColor,
                                    background: isDebit
                                      ? 'var(--color-warning-bg, #fff7ed)'
                                      : 'var(--color-success-bg, #f0fdf4)',
                                  }}
                                >
                                  {isDebit ? 'Debit Adjustment' : 'Credit Adjustment'}
                                </span>
                              </td>
                              <td
                                style={{
                                  ...tdStyle,
                                  whiteSpace: 'normal',
                                  color: 'var(--text-muted)',
                                }}
                              >
                                {detail || '—'}
                              </td>
                              <td
                                style={{
                                  ...tdStyle,
                                  textAlign: 'right',
                                  color: 'var(--text-placeholder)',
                                }}
                              >
                                —
                              </td>
                              <td
                                style={{
                                  ...tdStyle,
                                  textAlign: 'right',
                                  color: 'var(--text-placeholder)',
                                }}
                              >
                                —
                              </td>
                              <td
                                style={{
                                  ...tdStyle,
                                  textAlign: 'right',
                                  fontWeight: 'var(--font-weight-semi)',
                                  color: runningBalanceColor(balanceByRow.get(r.id) ?? 0),
                                }}
                              >
                                {fmtCents(balanceByRow.get(r.id) ?? 0)}
                              </td>
                              <td style={{ ...tdStyle, color: 'var(--text-placeholder)' }}>—</td>
                              <td style={tdStyle}>
                                <div
                                  style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}
                                >
                                  {r.invoice_id ? (
                                    <InvoiceLinkButton
                                      primary={primary}
                                      onClick={() => setDrawerInvoiceId(r.invoice_id)}
                                    />
                                  ) : null}
                                </div>
                              </td>
                            </tr>
                          );
                        }
                        // PRD.246 — for invoice-item rows Paid shows "—" because
                        // payment activity is carried by the chronological payment
                        // rows above. Manual rows keep their stored paid (PRD.245).
                        // Balance is the account-wide running balance after this
                        // charge (see runningBalanceByRow).
                        const isInvoiceItem = !!r.invoice_item_id;
                        const paidCents = reflectedPaidCents(r);
                        const running = balanceByRow.get(r.id) ?? 0;
                        // Rows auto-created from an invoice line are owned by
                        // that invoice — instead of edit/remove here, link to the
                        // invoice so it's edited/removed there and the two stay in
                        // sync. (Reflected adjustment rows are handled above.)
                        const isFromInvoice = !!r.invoice_id;
                        return (
                          <tr key={r.id}>
                            <td style={tdStyle}>{formatDate(r.date)}</td>
                            <td style={tdStyle}>{r.tooth_nos || '—'}</td>
                            <td style={tdStyle}>{r.procedure_name}</td>
                            {/* Notes column — each invoice row shows its own
                                notes: the latest per-line remark + the invoice's
                                billing note (both derived live from billing). */}
                            <td
                              style={{
                                ...tdStyle,
                                whiteSpace: 'normal',
                                verticalAlign: 'top',
                                maxWidth: 320,
                              }}
                            >
                              {isInvoiceItem &&
                                (() => {
                                  const remark = r.invoice_item_id
                                    ? itemRemarksById.get(r.invoice_item_id)
                                    : undefined;
                                  const invNote = r.invoice_id
                                    ? invoiceNoteById.get(r.invoice_id)
                                    : undefined;
                                  if (!remark && !invNote) return null;
                                  return (
                                    <div
                                      style={{
                                        fontSize: 'var(--font-size-xs)',
                                        color: 'var(--text-muted)',
                                      }}
                                    >
                                      {remark && <div>{remark}</div>}
                                      {invNote && (
                                        <div style={{ fontStyle: 'italic' }}>
                                          Invoice note: {invNote}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                              {fmtCents(r.amount_charged_cents ?? 0)}
                            </td>
                            <td
                              style={{
                                ...tdStyle,
                                textAlign: 'right',
                                color: isInvoiceItem ? 'var(--text-placeholder)' : '#16a34a',
                              }}
                            >
                              {isInvoiceItem ? '—' : fmtCents(paidCents)}
                            </td>
                            <td
                              style={{
                                ...tdStyle,
                                textAlign: 'right',
                                fontWeight: 'var(--font-weight-semi)',
                                color: runningBalanceColor(running),
                              }}
                            >
                              {fmtCents(running)}
                            </td>
                            <td style={tdStyle}>
                              {r.next_appointment_at
                                ? formatDate(r.next_appointment_at.split('T')[0])
                                : '—'}
                            </td>
                            <td style={tdStyle}>
                              <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                                {isFromInvoice ? (
                                  <InvoiceLinkButton
                                    primary={primary}
                                    onClick={() => setDrawerInvoiceId(r.invoice_id)}
                                  />
                                ) : (
                                  <>
                                    <button
                                      onClick={() => {
                                        setEditRecord(r);
                                        setDrawerOpen(true);
                                      }}
                                      title="Edit"
                                      style={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: 'var(--radius-sm)',
                                        border: 'none',
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        color: 'var(--text-muted)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: 0,
                                      }}
                                    >
                                      <NavIcon name="edit" />
                                    </button>
                                    <button
                                      onClick={() => handleDelete(r.id)}
                                      title="Remove"
                                      style={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: 'var(--radius-sm)',
                                        border: 'none',
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        color: 'var(--text-placeholder)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: 0,
                                      }}
                                    >
                                      <NavIcon name="x" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <TreatmentRecordDrawer
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            setEditRecord(null);
          }}
          primary={primary}
          editRecord={editRecord}
          onSave={handleSave}
          encounters={encounterOptions}
        />
      </div>

      {/* PRD.243 — Invoice detail drawer opened from invoice/adjustment row
          links. Same single-pane InvoicesTab the Billing tab uses, so edits
          made here flow through the invoice's own save path. */}
      <RightDrawer
        open={!!drawerInvoice}
        onClose={() => setDrawerInvoiceId(null)}
        title={
          drawerInvoice
            ? `Invoice ${drawerInvoice.invoice_no ?? drawerInvoice.id.slice(0, 8)}`
            : 'Invoice'
        }
        icon="file-text"
        expandable
        initialExpanded
        expandedWidth="min(1200px, 90%)"
        contentPadding="var(--sp-4)"
      >
        {drawerInvoice && (
          <InvoicesTab
            key={drawerInvoice.id}
            patientId={patientId}
            initialInvoiceId={drawerInvoice.id}
            hideHeader
            singlePane
          />
        )}
      </RightDrawer>
    </div>
  );
}

// ─── Day notes content (shared by the standalone notes row and the charged
// row that hosts the day's notes) ───

function DayNotesContent({ notes }: { notes: EncounterNotePreviewRow[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      {notes.map((n, ni) => (
        <NotePreview key={n.created_at ?? `${ni}`} note={n} marker="dot" />
      ))}
    </div>
  );
}

// ─── Invoice link button (PRD.243) ───

function InvoiceLinkButton({ primary, onClick }: { primary: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title="Open the linked invoice"
      style={{
        height: 26,
        padding: '0 12px',
        borderRadius: 'var(--radius-md)',
        border: `1px solid ${primary}`,
        background: 'transparent',
        color: primary,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-semi)',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      Open
    </button>
  );
}
